import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Mail,
  Linkedin,
  Youtube,
  ArrowUpRight,
  Check,
  Loader2,
} from "lucide-react";
import { PROFILE } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "";
// Hostinger PHP endpoint (preferred for static deploy)
const CONTACT_ENDPOINT =
  process.env.REACT_APP_CONTACT_ENDPOINT || "/contact.php";

const INITIAL = { name: "", email: "", subject: "", message: "", company: "" };

export default function Contact() {
  const [values, setValues] = useState(INITIAL);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (k) => (e) =>
    setValues((v) => ({ ...v, [k]: e.target.value }));

  const validate = () => {
    if (!values.name.trim()) return "Please share your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email))
      return "Please share a valid email.";
    if (!values.subject.trim()) return "A short subject helps a lot.";
    if (values.message.trim().length < 10)
      return "Please add a few more details in your message.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setErrorMsg(v);
      setStatus("error");
      return;
    }
    setErrorMsg("");
    setStatus("loading");

    const endpoint = BACKEND_URL ? `${API}/contact` : CONTACT_ENDPOINT;

    try {
      const res = await axios.post(endpoint, values, {
        timeout: 25000,
        headers: { "Content-Type": "application/json" },
      });
      if (res.data?.ok) {
        setStatus("success");
        setValues(INITIAL);
      } else {
        setStatus("error");
        setErrorMsg(res.data?.detail || "Something went wrong. Please try again.");
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "";
      setStatus("error");
      setErrorMsg(
        typeof detail === "string" && detail
          ? detail
          : `We couldn't send your message. Email me at ${PROFILE.contact.email}.`
      );
    }
  };

  return (
    <section
      id="contact"
      className="relative py-28 md:py-36"
      data-testid="contact-section"
    >
      <div className="section-x mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left */}
          <div className="lg:col-span-5">
            <p className="section-eyebrow">06 — Contact Me</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
              Let&apos;s build <br className="hidden md:block" />
              <span className="text-ink/50">better QA teams together.</span>
            </h2>
            <p className="mt-6 text-ink/60 max-w-md leading-relaxed">
              Consulting engagements, corporate training programs, keynote
              invites, or a friendly hello — my inbox is open.
            </p>

            <ul className="mt-10 space-y-3">
              <ContactRow
                icon={Mail}
                label="Email"
                value={PROFILE.contact.email}
                href={`mailto:${PROFILE.contact.email}`}
                testid="contact-email"
              />
              <ContactRow
                icon={Linkedin}
                label="LinkedIn"
                value="linkedin.com/in/chiragkhimani"
                href={PROFILE.contact.linkedin}
                testid="contact-linkedin"
              />
              <ContactRow
                icon={Youtube}
                label="YouTube"
                value="youtube.com/c/chiragkhimani"
                href={PROFILE.contact.youtube}
                testid="contact-youtube"
              />
            </ul>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              noValidate
              className="relative glass rounded-3xl p-6 md:p-10"
              data-testid="contact-form"
              aria-labelledby="contact-form-title"
            >
              <h3
                id="contact-form-title"
                className="font-display text-2xl md:text-3xl tracking-tight"
              >
                Send a message
              </h3>
              <p className="mt-1 text-ink/50 text-sm">
                Usually respond within 24 hours.
              </p>

              {/* Honeypot */}
              <input
                type="text"
                name="company"
                autoComplete="off"
                tabIndex={-1}
                value={values.company}
                onChange={update("company")}
                aria-hidden="true"
                className="hidden"
              />

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  id="name"
                  label="Full name"
                  autoComplete="name"
                  value={values.name}
                  onChange={update("name")}
                  testid="input-name"
                />
                <Field
                  id="email"
                  type="email"
                  label="Email"
                  autoComplete="email"
                  value={values.email}
                  onChange={update("email")}
                  testid="input-email"
                />
              </div>
              <div className="mt-4">
                <Field
                  id="subject"
                  label="Subject"
                  autoComplete="off"
                  value={values.subject}
                  onChange={update("subject")}
                  testid="input-subject"
                />
              </div>
              <div className="mt-4">
                <Field
                  id="message"
                  label="Message"
                  multiline
                  autoComplete="off"
                  value={values.message}
                  onChange={update("message")}
                  testid="input-message"
                />
              </div>

              <AnimatePresence>
                {status === "error" && errorMsg && (
                  <motion.p
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-4 text-sm text-rose-300"
                    role="alert"
                    data-testid="contact-error"
                  >
                    {errorMsg}
                  </motion.p>
                )}
              </AnimatePresence>

              <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-xs text-ink/55">
                  By submitting, you agree to a friendly reply from{" "}
                  <span className="text-ink/70">Chirag</span>.
                </p>
                <SubmitButton status={status} />
              </div>
            </form>
          </div>
        </div>
      </div>

      <SuccessOverlay
        visible={status === "success"}
        onClose={() => setStatus("idle")}
      />
    </section>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const Comp = href ? "a" : "div";
  return (
    <li>
      <Comp
        {...(href ? { href, target: "_blank", rel: "noreferrer" } : {})}
        className="group flex items-center gap-4 rounded-2xl border border-ink/[0.08] bg-ink/[0.02] px-4 py-3.5 hover:border-ink/20 hover:bg-ink/[0.04] transition-colors"
        data-testid={testid}
      >
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-ink/10 bg-ink/[0.03] text-ink/85">
          <Icon size={16} />
        </span>
        <span className="flex-1">
          <span className="block text-[10px] uppercase tracking-[0.28em] text-ink/55 font-mono">
            {label}
          </span>
          <span className="block text-ink/85 text-[15px]">{value}</span>
        </span>
        {href && (
          <ArrowUpRight
            size={14}
            className="text-ink/40 group-hover:text-accent-cyan transition-colors"
          />
        )}
      </Comp>
    </li>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  type = "text",
  multiline = false,
  autoComplete,
  testid,
}) {
  const hasValue = value && value.length > 0;
  const Input = multiline ? "textarea" : "input";
  return (
    <label htmlFor={id} className="relative block">
      <span
        className={`pointer-events-none absolute left-4 z-10 origin-left transition-all duration-300 ${
          hasValue
            ? "top-2 scale-90 text-[10px] uppercase tracking-[0.28em] text-ink/50 font-mono"
            : "top-4 text-ink/55"
        }`}
      >
        {label}
      </span>
      <Input
        id={id}
        name={id}
        type={multiline ? undefined : type}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        rows={multiline ? 5 : undefined}
        data-testid={testid}
        className={`w-full rounded-2xl border border-ink/10 bg-ink/[0.04] px-4 pt-7 pb-3 text-ink placeholder-transparent outline-none focus:border-accent-cyan/60 focus:bg-ink/[0.07] transition-colors ${
          multiline ? "resize-none min-h-[140px]" : ""
        }`}
      />
    </label>
  );
}

function SubmitButton({ status }) {
  const loading = status === "loading";
  const success = status === "success";
  return (
    <button
      type="submit"
      disabled={loading}
      data-testid="contact-submit"
      className="relative inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 text-white px-6 py-3.5 text-sm font-semibold shadow-[0_8px_30px_rgba(139,92,246,0.35)] hover:shadow-[0_12px_40px_rgba(139,92,246,0.55)] disabled:opacity-70 transition-shadow overflow-hidden"
    >
      {loading ? (
        <>
          <Loader2 size={16} className="animate-spin" /> Sending…
        </>
      ) : success ? (
        <>
          <Check size={16} /> Sent
        </>
      ) : (
        <>
          Send message
          <ArrowUpRight size={16} />
        </>
      )}
    </button>
  );
}

function SuccessOverlay({ visible, onClose }) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-md flex items-center justify-center px-6"
          data-testid="contact-success-overlay"
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative glass rounded-3xl p-8 md:p-12 max-w-md text-center"
          >
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-accent-cyan/40 bg-accent-cyan/10 glow-cyan">
              <Check size={28} className="text-accent-cyan" />
            </div>
            <h3 className="mt-6 font-display text-3xl tracking-tight">
              Message received.
            </h3>
            <p className="mt-3 text-ink/60">
              Thanks for reaching out — I&apos;ll get back to you within 24
              hours.
            </p>
            <button
              onClick={onClose}
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-ink/[0.05] px-5 py-2.5 text-sm hover:border-accent-cyan/60"
              data-testid="contact-success-close"
            >
              Close
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
